/* eslint-disable react/jsx-props-no-multi-spaces */
import { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SendIcon from "@mui/icons-material/Send";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import toast from "react-hot-toast";
import * as moment from "moment";
import { sessionApi } from "../../../api/sessionApi";

/**
 * Bouton « Feuille d'émargement » de la fiche session.
 *
 * Deux actions, toutes deux au format PDF : télécharger, ou envoyer au
 * formateur de la session. Le back-end produit une feuille par famille de
 * financeur (DPC / FIF-PL) et par vacation : une journée de formation = matin
 * + après-midi, une demi-journée = une seule feuille, deux jours = quatre
 * feuilles par financeur.
 */
const SessionEmargementButton = ({ sessionId, sx, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [email, setEmail] = useState("");

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleDownload = async () => {
    closeMenu();
    setDownloading(true);
    try {
      const data = await sessionApi.downloadEmargement(
        sessionId,
        localStorage.getItem("accessToken")
      );
      const count = data.sheets ? data.sheets.length : 1;
      toast.success(
        `${count} feuille${count > 1 ? "s" : ""} d'émargement téléchargée${
          count > 1 ? "s" : ""
        } !`
      );
      // modèle Word absent pour un financeur : ses apprenants n'ont pas de feuille
      if (data.warning) {
        toast.error(data.warning, { duration: 10000 });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Une erreur est survenue lors du téléchargement !");
    }
    setDownloading(false);
  };

  const handleOpenSend = async () => {
    closeMenu();
    setLoadingPlan(true);
    setPlan(null);
    try {
      const data = await sessionApi.getEmargementPlan(
        sessionId,
        localStorage.getItem("accessToken")
      );

      // La feuille part au formateur de la session : sans lui, ou sans son
      // adresse, on renvoie l'utilisateur vers la donnée à compléter plutôt
      // que d'ouvrir un dialogue inutilisable.
      if (data && data.available && !data.recipient) {
        toast.error("Veuillez remplir la case du formateur de la session.");
        setLoadingPlan(false);
        return;
      }
      if (data && data.available && !data.recipient.email) {
        toast.error(
          `Veuillez saisir l'email du formateur ${data.recipient.name} dans sa fiche intervenant.`
        );
        setLoadingPlan(false);
        return;
      }

      setPlan(data);
      if (data && data.recipient && data.recipient.email) {
        setEmail(data.recipient.email);
      }
      setSendOpen(true);
    } catch (err) {
      console.error(err);
      setPlan({ available: false, message: "Impossible de préparer les feuilles d'émargement." });
      setSendOpen(true);
    }
    setLoadingPlan(false);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const data = await sessionApi.sendEmargement(
        sessionId,
        email,
        plan && plan.recipient ? plan.recipient.name : "",
        localStorage.getItem("accessToken")
      );
      if (data && data.status === 200) {
        toast.success(data.message || "Feuilles d'émargement envoyées !");
        if (data.warning) {
          toast.error(data.warning, { duration: 10000 });
        }
        setSendOpen(false);
      } else {
        toast.error((data && data.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue lors de l'envoi !");
    }
    setSending(false);
  };

  const busy = downloading || sending || loadingPlan;
  const sheets = plan && plan.available ? plan.sheets : [];

  return (
    <>
      <Button
        onClick={openMenu}
        sx={sx}
        variant="outlined"
        disabled={disabled || busy}
        startIcon={
          busy ? <CircularProgress size={14} /> : <FactCheckOutlinedIcon fontSize="small" />
        }
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
      >
        Feuille d&apos;émargement
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Télécharger (PDF)"
            secondary="Une feuille par financeur et par demi-journée"
          />
        </MenuItem>
        <MenuItem onClick={handleOpenSend}>
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Envoyer au formateur" secondary="Par e-mail, en PDF" />
        </MenuItem>
      </Menu>

      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Envoyer la feuille d&apos;émargement au formateur</DialogTitle>
        <DialogContent>
          {loadingPlan && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          )}

          {!loadingPlan && plan && !plan.available && (
            <Alert
              severity={plan.missing && plan.missing.length ? "error" : "info"}
              sx={{ my: 1 }}
            >
              {plan.message || "Aucune feuille d'émargement à générer pour cette session."}
            </Alert>
          )}

          {!loadingPlan && plan && plan.available && (
            <>
              {plan.warning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {plan.warning}
                </Alert>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {sheets.length} feuille{sheets.length > 1 ? "s" : ""} seront jointes :
              </Typography>
              <Box sx={{ mb: 2 }}>
                {sheets.map((s) => (
                  <Box
                    key={s.filename}
                    sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
                  >
                    <Chip size="small" label={s.group_label} color="primary" variant="outlined" />
                    <Typography variant="body2">
                      {moment(s.date).format("DD/MM/YYYY")} — {s.slot_label} ({s.start} - {s.end})
                      {" · "}
                      {s.learners} apprenant{s.learners > 1 ? "s" : ""}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TextField
                autoFocus
                fullWidth
                label="Adresse e-mail du formateur"
                helperText={
                  plan.recipient
                    ? `Formateur de la session : ${plan.recipient.name}`
                    : "Saisissez l'adresse du destinataire"
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            color="primary"
            variant="contained"
            disableElevation
            disabled={sending || loadingPlan || !plan || !plan.available || !email}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SessionEmargementButton.propTypes = {
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sx: PropTypes.object,
  disabled: PropTypes.bool,
};

export default SessionEmargementButton;
